import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { PolicySetting } from '@kirin/api-client/contracts/policies';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import {
  removeItemByIndex,
  SEVERITY_OPTIONS,
  useSortedItemsWithIndices,
} from '../helpers';
import { SeverityBadge } from './SeverityBadge';

/**
 * Map of field keys to their placeholder text
 */
const FIELD_PLACEHOLDER_MAP: Record<string, string> = {
  pattern: 'e.g., anonymous',
  permission: 'e.g., file://*',
  reason: 'Why this is flagged',
};

/**
 * Get placeholder text for a field key.
 * Returns the mapped placeholder or a default fallback.
 */
function getFieldPlaceholder(key: string): string {
  return FIELD_PLACEHOLDER_MAP[key] || `Enter ${key}`;
}

interface ArrayItem {
  pattern?: string;
  permission?: string;
  reason?: string;
  severity?: string;
  [key: string]: unknown;
}

interface ArrayObjectSettingProps {
  setting: PolicySetting;
  value: ArrayItem[];
  onChange: (value: ArrayItem[]) => void;
  disabled?: boolean;
}

export function ArrayObjectSetting({
  setting,
  value,
  onChange,
  disabled = false,
}: ArrayObjectSettingProps) {
  const [newItem, setNewItem] = useState<ArrayItem>({});

  const properties = setting.validation?.properties || {};
  const items = Array.isArray(value) ? value : [];

  // Sort items with their original indices
  const sortedWithIndices = useSortedItemsWithIndices(items);

  const validateItem = (item: ArrayItem): boolean => {
    // Validate each property against its maxLength constraint
    for (const [key, propConfig] of Object.entries(properties)) {
      const value = String(item[key] || '');
      const maxLength = (propConfig as { maxLength?: number })?.maxLength;

      // Hardcoded validation for permission field (50 characters)
      if (key === 'permission' && value.length > 50) {
        return false;
      }

      // Hardcoded validation for reason field (200 characters)
      if (key === 'reason' && value.length > 200) {
        return false;
      }

      if (maxLength && value.length > maxLength) {
        return false;
      }
    }

    return true;
  };

  const addItem = () => {
    if (Object.keys(newItem).length === 0) return;

    const itemToAdd = { ...newItem };

    // Handle default values for select fields that show a default but aren't in newItem yet
    if (properties.severity && !itemToAdd.severity) {
      itemToAdd.severity = 'medium';
    }

    // Validate before adding
    if (!validateItem(itemToAdd)) {
      return;
    }

    onChange([...items, itemToAdd]);
    setNewItem({});
  };

  const removeItem = (originalIndex: number) => {
    onChange(removeItemByIndex(items, originalIndex));
  };

  const updateNewItem = (key: string, val: string) => {
    setNewItem(prev => ({ ...prev, [key]: val }));
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Badge variant="outline">Current: {items.length} items</Badge>
      </div>

      {/* Table Layout */}
      <div className="border border-table-border rounded-md overflow-hidden text-xs">
        {/* Table header */}
        <div className="grid grid-cols-12 gap-2 bg-muted min-h-10 items-center border-b">
          <div className="col-span-4 px-3 font-medium text-foreground/80 whitespace-nowrap">
            {setting.key === 'dangerousPermissions' ? 'Permission' : 'Pattern'}
          </div>
          <div className="col-span-5 px-3 font-medium text-foreground/80 whitespace-nowrap">
            Reason
          </div>
          <div className="col-span-2 px-3 font-medium text-foreground/80 whitespace-nowrap">
            Severity
          </div>
          <div className="col-span-1"></div>
        </div>

        {/* Table rows */}
        {items.length > 0 ? (
          <div className="scrollable-medium">
            {sortedWithIndices.map(({ item, originalIndex }) => (
              <div
                key={`${item.pattern || item.permission || ''}-${item.description || item.reason || ''}-${item.severity || ''}-${originalIndex}`}
                className="grid grid-cols-12 gap-2 min-h-12 items-center border-b last:border-b-0 hover:bg-muted/50"
              >
                {/* Pattern/Permission field */}
                <div
                  className="col-span-4 px-3 py-2 truncate"
                  title={String(item.permission || item.pattern || '')}
                >
                  <span>{String(item.permission || item.pattern || '')}</span>
                </div>

                {/* Reason/Description field */}
                <div
                  className="col-span-5 px-3 py-2 truncate"
                  title={String(item.description || item.reason || '')}
                >
                  <span>{String(item.description || item.reason || '')}</span>
                </div>

                {/* Severity field */}
                <div className="col-span-2 px-3 py-2">
                  <SeverityBadge
                    riskLevel={String(item.severity || 'medium')}
                    showIcon
                    className="text-xs"
                  />
                </div>

                {/* Delete button */}
                <div className="col-span-1 px-3 py-2 flex justify-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    onClick={() => removeItem(originalIndex)}
                    className="h-7 w-7 p-0"
                    disabled={disabled}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Empty state */
          <div className="px-3 py-6 text-center text-muted-foreground">
            No{' '}
            {setting.key === 'dangerousPermissions'
              ? 'permissions'
              : 'patterns'}{' '}
            configured
          </div>
        )}
      </div>

      {/* Add New Item - Table Layout */}
      <div className="form-input-group">
        {Object.entries(properties).map(([key]: [string, unknown]) => {
          const colSpan =
            key === 'pattern' || key === 'permission'
              ? 'col-span-4'
              : key === 'reason'
                ? 'col-span-5'
                : 'col-span-2';

          return (
            <div key={key} className={colSpan}>
              {key === 'severity' ? (
                <select
                  value={String(newItem[key] || 'medium')}
                  onChange={e => updateNewItem(key, e.target.value)}
                  className="form-select-compact"
                  disabled={disabled}
                >
                  {SEVERITY_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : (
                <Input
                  value={String(newItem[key] || '')}
                  onChange={e => updateNewItem(key, e.target.value)}
                  placeholder={getFieldPlaceholder(key)}
                  className="form-input-compact"
                  disabled={disabled}
                  maxLength={
                    key === 'permission'
                      ? 50 // Hardcoded limit for permission field
                      : key === 'reason'
                        ? 200 // Hardcoded limit for reason field
                        : (properties[key] as { maxLength?: number })?.maxLength
                  }
                />
              )}
            </div>
          );
        })}
        <div className="col-span-1 flex justify-center">
          <Button
            onClick={addItem}
            size="sm"
            type="button"
            className="btn-action-compact"
            disabled={Object.values(newItem).some(v => !v) || disabled}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="text-xs text-muted-foreground">
        {items.length} items configured
      </div>
    </div>
  );
}
